import {
  I2cListItem,
  I2cRadio,
  I2cRadioGroup,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { pullApiOptions, inHouseDevelopmentTeamOptions, isPciCompliantOptions } from "./stringUtils";
import { validationSchemaPullAPIs } from "./ValidationSchema";

const PullAPIs = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaPullAPIs),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c: "",
      web_services_pull_api_do_you_have_an_in_house_development_team: "",
      web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c",
    "web_services_pull_api_do_you_have_an_in_house_development_team",
    "web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      6: {
        ...prev[6],
        "6_0": { ...values },
      },
    }));
  }, [methods, setAddFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[6]?.["6_0"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("6_0", true);
    } else {
      setFormValidStates("6_0", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleRadioChange = (fieldName, value, fieldsToReset = {}) => {
    methods.setValue(fieldName, value, { shouldValidate: true });
    
    // Clear dependent fields if needed
    Object.keys(fieldsToReset).forEach(key => {
      methods.setValue(key, fieldsToReset[key]);
    });
    
    handleValueChanges();
  };

  const handleInHouseDevelopmentTeamChange = (e) => {
    const value = e.target?.value || e;
    handleRadioChange(
      "web_services_pull_api_do_you_have_an_in_house_development_team",
      value,
      value !== "no" ? {
        web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant: ""
      } : {}
    );
  };

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div className="sub-section" i2c-grid="12">
          <I2cRadioGroup
            label="Please select version of Pull API you would like to opt from i2c"
            value={methods.watch("web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c")}
            onI2cChange={(e) => handleRadioChange("web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c", e.target?.value || e)}
          >
            <div i2c-grid="grid grid-sm">
              {pullApiOptions.map((radio, index) => (
                <I2cListItem
                  key={index}
                  i2c-grid="6@md"
                  variant="receipt"
                  label={radio.label}
                  type="checkbox"
                >
                  <I2cRadio slot="prefix" filled value={radio.value} />
                </I2cListItem>
              ))}
            </div>
          </I2cRadioGroup>
          {errors.web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c && (
            <ErrorMessage show={true} message={errors.web_services_pull_api_select_version_of_pull_api_you_would_like_to_opt_from_i2c.message} />
          )}
        </div>
        <div className="sub-section" i2c-grid="12">
          <I2cRadioGroup
            label="Do you have an in-house development team?"
            value={methods.watch("web_services_pull_api_do_you_have_an_in_house_development_team")}
            onI2cChange={handleInHouseDevelopmentTeamChange}
          >
            <div i2c-grid="grid grid-sm">
              {inHouseDevelopmentTeamOptions.map((radio, index) => (
                <I2cListItem
                  key={index}
                  i2c-grid="6@md"
                  variant="receipt"
                  label={radio.label}
                  type="checkbox"
                >
                  <I2cRadio slot="prefix" filled value={radio.value} />
                </I2cListItem>
              ))}
            </div>
          </I2cRadioGroup>
          {errors.web_services_pull_api_do_you_have_an_in_house_development_team && (
            <ErrorMessage show={true} message={errors.web_services_pull_api_do_you_have_an_in_house_development_team.message} />
          )}
        </div>
        {methods.watch("web_services_pull_api_do_you_have_an_in_house_development_team") === "no" && (
          <div className="sub-section mb-16" i2c-grid="12">
            <I2cRadioGroup
              label="Please confirm if the 3rd party vendor is PCI Compliant"
              value={methods.watch("web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant")}
              onI2cChange={(e) =>
                handleRadioChange("web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant", e.target?.value || e)
              }
            >
              <div i2c-grid="grid grid-sm">
                {isPciCompliantOptions.map((radio, index) => (
                  <I2cListItem
                    key={index}
                    i2c-grid="6@md"
                    variant="receipt"
                    label={radio.label}
                    type="checkbox"
                  >
                    <I2cRadio slot="prefix" filled value={radio.value} />
                  </I2cListItem>
                ))}
              </div>
            </I2cRadioGroup>
            {errors.web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant && (
              <ErrorMessage show={true} message={errors.web_services_pull_api_confirm_if_third_party_vendor_is_pci_compliant.message} />
            )}
          </div>
        )}
        {/* <div className="sub-section" i2c-grid="12">
          {inHouseDevelopmentTeamOptionValue === "no" &&
            isPciCompliantOptionValue === "yes" && (
              <>
                <div className="custom-label-light mb-16">
                  Please Attach Letter of Compliance
                </div>

                <I2cFileUpload
                  autoProcessQueue={false}
                  no-mobile-controls
                  className="customFileUpload"
                  setAcceptedFiles=".pdf, .xlsx, .doc"
                  icon="linear-document-upload"
                  label="Click to upload"
                  caption="or drag and drop"
                  name="additional_agreement_content_type_customized_file"
                  message="PDF, .xlsx, or .doc (max. 1MB)"
                  onI2cFileRemoved={(e) => {}}
                  onI2cAddedFile={(e) => {}}
                />

                <div className="custom-label-light pt-12">
                  <i>
                    A copy of the letter of compliance will be required for
                    record purposes.
                  </i>
                </div>
              </>
            )}
        </div> */}
      </div>
    </React.Fragment>
  );
};

export default PullAPIs;
